import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import usePrevious from 'hooks/use-previous';
import { useTrolleyAnalytics } from 'hooks/use-trolley-analytics';
import { useWtrSelector } from 'redux/hooks';
import { getCustomerId, getCustomerOrderId } from 'redux/modules/sessions/selectors';
import getLoadingAdd from 'redux/modules/trolley/selectors/get-loading-add';
import { useAnalyticsContext } from 'components/Analytics/AnalyticsContext';

type PreviousLocation =
  | {
      pathname: string;
      search: string;
    }
  | undefined;

const TrolleyAnalyticsReporter = () => {
  const location = useLocation();
  const { trackAbandonTrolleyUpdateEvent } = useTrolleyAnalytics();
  const trolleyAddLoading = useWtrSelector(getLoadingAdd);
  const customerId = useWtrSelector(getCustomerId);
  const customerOrderId = useWtrSelector(getCustomerOrderId);
  const previousLocation = usePrevious(location) as unknown as PreviousLocation;

  const analyticsConfig = useAnalyticsContext();

  const reportAbandonTrolleyUpdate = useCallback(() => {
    if (document.visibilityState === 'hidden' && trolleyAddLoading) {
      trackAbandonTrolleyUpdateEvent(
        window.location.href,
        analyticsConfig.clientId || null,
        analyticsConfig.sessionId || null,
        customerId,
        customerOrderId,
      );
    }
  }, [
    analyticsConfig.clientId,
    analyticsConfig.sessionId,
    customerId,
    customerOrderId,
    trackAbandonTrolleyUpdateEvent,
    trolleyAddLoading,
  ]);

  useEffect(() => {
    if (!analyticsConfig.clientId || !analyticsConfig.sessionId) {
      return () => {};
    }

    document.addEventListener('visibilitychange', reportAbandonTrolleyUpdate);

    return () => {
      document.removeEventListener('visibilitychange', reportAbandonTrolleyUpdate);
    };
  }, [
    analyticsConfig,
    analyticsConfig.clientId,
    analyticsConfig.sessionId,
    reportAbandonTrolleyUpdate,
  ]);

  useEffect(() => {
    if (!analyticsConfig.clientId || !analyticsConfig.sessionId) {
      return;
    }

    if (previousLocation && previousLocation.pathname !== location.pathname && trolleyAddLoading) {
      trackAbandonTrolleyUpdateEvent(
        window.location.origin + previousLocation.pathname + previousLocation.search,
        analyticsConfig.clientId || null,
        analyticsConfig.sessionId || null,
        customerId,
        customerOrderId,
      );
    }
  }, [
    analyticsConfig,
    analyticsConfig.clientId,
    analyticsConfig.sessionId,
    customerId,
    customerOrderId,
    location.pathname,
    previousLocation,
    trackAbandonTrolleyUpdateEvent,
    trolleyAddLoading,
  ]);

  return null;
};

export default TrolleyAnalyticsReporter;
